import axios from 'axios'
import { FaBriefcase, FaCalculator, FaCalendarAlt, FaClipboardList } from 'react-icons/fa'

import { GlobalSearchItem } from '../../types/globalSearch.types'

/**
 * What the header search and the full search results page share: the quick
 * search (GET /search/quick), the shape of a result, where each kind opens,
 * and how matches are highlighted - so the two always find and show the same
 * things.
 */

const apiURL = process.env.REACT_APP_API_URL

export const MIN_CHARS = 2
// Named from when this was a preview; kept so people keep their list.
const RECENT_KEY = 'previewRecentlyOpened'
const RECENT_MAX = 6

export type Kind = 'Job' | 'Quote' | 'Enquiry' | 'Diary'

export const KINDS: { kind: Kind; plural: string; icon: JSX.Element; tone: string }[] = [
  { kind: 'Job', plural: 'Jobs', icon: <FaBriefcase />, tone: 'lime' },
  { kind: 'Quote', plural: 'Quotes', icon: <FaCalculator />, tone: 'teal' },
  { kind: 'Enquiry', plural: 'Enquiries', icon: <FaClipboardList />, tone: 'violet' },
  { kind: 'Diary', plural: 'Diary', icon: <FaCalendarAlt />, tone: 'orange' },
]

export const KIND_INFO = Object.fromEntries(KINDS.map((k) => [k.kind, k])) as Record<Kind, (typeof KINDS)[number]>

/** A result as the search needs it, also what "recently opened" remembers. */
export interface Hit {
  key: string
  kind: Kind
  id: number
  appointmentId?: number
  ref: string
  title: string
  detail: string
  place: string
  /** Who to contact - or, for a diary entry, who's on it - shown on the full results page. */
  contact?: string
  createdDate?: string
  /** When a diary entry is. */
  startDate?: string
  endDate?: string
}

const PREFIX: Record<Kind, string> = { Job: 'J', Quote: 'Q', Enquiry: 'E', Diary: 'J' }

const toHit = (item: GlobalSearchItem): Hit | null => {
  const kind = item.type as Kind
  if (!KIND_INFO[kind]) return null
  const ref = item.ref ? `${PREFIX[kind]}${String(item.ref).padStart(6, '0')}` : ''
  return {
    key: `${kind}-${item.id}-${item.appointmentId ?? ''}`,
    kind,
    id: item.id,
    appointmentId: item.appointmentId,
    ref,
    title: item.account || item.contact || 'No client',
    detail: item.description || '',
    place: [item.address, item.postCode].filter(Boolean).join(', '),
    contact:
      kind === 'Diary'
        ? item.crew?.length
          ? `Crew: ${item.crew.join(', ')}`
          : 'No one on it'
        : [item.contact, item.phone, item.email].filter(Boolean).join(' · '),
    createdDate: item.createdDate,
    startDate: item.startDate ?? undefined,
    endDate: item.endDate ?? undefined,
  }
}

/** Where a result opens. */
export const pathFor = (hit: Hit) => {
  switch (hit.kind) {
    case 'Enquiry':
      return `/enquiries/?id=${hit.id}`
    case 'Quote':
      return `/quotes/?id=${hit.id}`
    case 'Job':
      return `/jobs/?id=${hit.id}`
    case 'Diary':
      return `/diary/?jobId=${hit.id}&appointmentId=${hit.appointmentId}`
  }
}

/** The full results page for a search. */
export const resultsPathFor = (q: string) => `/search?q=${encodeURIComponent(q.trim())}`

export const readRecent = (): Hit[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]')
    return Array.isArray(parsed) ? parsed.filter((h) => h && KIND_INFO[h.kind as Kind]) : []
  } catch {
    return []
  }
}

export const rememberRecent = (hit: Hit) => {
  try {
    const next = [hit, ...readRecent().filter((h) => h.key !== hit.key)].slice(0, RECENT_MAX)
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    // Not remembering is fine.
  }
}

export const clearRecent = () => {
  try {
    window.localStorage.removeItem(RECENT_KEY)
  } catch {
    // Nothing to clear.
  }
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Bolds each word of the search where it appears in the text. */
export const Highlight = ({ text, query }: { text: string; query: string }) => {
  const words = query.trim().split(/\s+/).filter((w) => w.length >= MIN_CHARS)
  if (!text || words.length === 0) return <>{text}</>
  const pattern = new RegExp(`(${words.map(escapeRegExp).join('|')})`, 'ig')
  return (
    <>
      {text.split(pattern).map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} className='psr-mark'>
            {part}
          </mark>
        ) : (
          part
        )
      )}
    </>
  )
}

interface QuickSearchResponse {
  results: GlobalSearchItem[]
  /** Kinds with more matches than were returned. */
  more: string[]
  /** Every match of each kind, when asked for. */
  counts?: Record<string, number>
}

export interface QuickSearchOptions {
  type?: Kind
  take: number
  skip?: number
  /** Count every match of each kind as well. */
  counts?: boolean
  /** Search diary entries too, when no type is given. */
  diary?: boolean
}

export const quickSearch = async (
  q: string,
  signal: AbortSignal,
  { type, take, skip = 0, counts = false, diary = false }: QuickSearchOptions
) => {
  const token = localStorage.getItem('authToken')
  const response = await axios.get<QuickSearchResponse>(`https://${apiURL}/search/quick`, {
    params: {
      q,
      take,
      ...(type ? { type } : {}),
      ...(skip ? { skip } : {}),
      ...(counts ? { counts } : {}),
      ...(diary ? { diary } : {}),
    },
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    signal,
  })
  return {
    hits: (response.data?.results ?? []).map(toHit).filter((h): h is Hit => h !== null),
    more: (response.data?.more ?? []) as Kind[],
    counts: (response.data?.counts ?? {}) as Partial<Record<Kind, number>>,
  }
}
